/*
<DataTable /> Provides a reusable table to attach show and interact with query results

  Props:
      query   API query endpoint. ex: 'people/list'

      params  Query parameters to send along with the filters/sort options from the table (optional)

      cols    Column configuration. List of {header, key | render} objects
              this can be extended to inject custom rendering handlers and custom filter/sorting per column
              render, if provided, is expected to be a function that takes in the item and returns a React Fragment
              otherwise, key is expected to be a key defined in the object already prepared for rendering

      actions string | ReactClass | { component: ReactClass, ...props}
              Content to render into the actions column (optional)

      actions_header  string | ReactClass | { component: ReactClass, ...props}
                      Content to render in the header above the actions column

      empty   Message to provide when query returns no results

      onUpdate function to call with updated data rows when table updates (optional)

      ...row_props Properties starting with "row_" will be stripped of the prefix and passed to each
                   rendered data <tr />. Useful for attaching handlers for click and hover.

      ...props  Other props are passed to the containing <table />
                useful for className injection or event handling


  Examples:
      <DataTable query='orders/list'
                 params={type: 'SFTP'}
                 cols={table_columns}
                 actions={table_actions}
                 empty='No Orders Found' />
*/

import React, { useState, useEffect, useCallback, useRef, useContext } from 'react';
import classNames from 'classnames/bind';
import debounce from 'core/utils/debounce';
import stringOrComponent from "../utils/string-or-component";
import SessionContext from 'SessionContext';
import { Table, Dropdown, DropdownToggle, DropdownMenu, DropdownItem, Button, ButtonGroup, Input, InputGroup, InputGroupText } from 'reactstrap';

export default function DataTable(props) {
  const { cols, actions, empty, extraClasses, actions_header, onUpdate, update_data, getRowCount, ...rest_props } = props;
  const session = useContext(SessionContext);

  const getInitialPageSize = () => {
    const lsListCount = JSON.parse(localStorage.getItem('am:data_page_size')) || {};
    return lsListCount[props.query] || 100;
  };

  const [sortBy, setSortBy] = useState(() => props.params?.sort_by ?? null);
  const [sortAsc, setSortAsc] = useState(() => props.params?.sort_asc ?? null);
  const [companyId] = useState(() => props.params?.am_company_id ?? null);
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState(props.params || {});
  const [page, setPage] = useState(0);
  const [pageSize, setPageSizeSt] = useState(getInitialPageSize);
  const [total, setTotal] = useState(null);
  const [totalPages, setTotalPages] = useState(null);
  const [canPreviousPage, setCanPreviousPage] = useState(false);
  const [canNextPage, setCanNextPage] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [listHeight, setListHeight] = useState(800);
  const prevUpdateDataValueRef = useRef(null);
  const isMountedRef = useRef(true);

  // Refs so the debounced function always reads latest values without recreating
  const stateRef = useRef({});
  stateRef.current = { sortBy, sortAsc, companyId, filters, page, pageSize };
  const sessionRef = useRef(session);
  sessionRef.current = session;
  const onUpdateRef = useRef(onUpdate);
  onUpdateRef.current = onUpdate;
  const queryRef = useRef(props.query);
  queryRef.current = props.query;

  // Stable debounced updateData — reads state via refs to avoid stale closures
  const updateData = useRef(
    debounce(() => {
      const query = queryRef.current;
      if (query && query === "placeholder") {
        setData([]);
        return;
      }

      const { sortBy, sortAsc, companyId, filters, page, pageSize } = stateRef.current;
      const _session = sessionRef.current;
      const sortAndFilterValues = {
        filters: JSON.stringify(filters),
        sort_by: sortBy || '',
        sort_dir: sortAsc ? 'asc' : 'desc'
      };
      const _company_id = companyId || _session.appState.activeCompany.id;
      const params = Object.assign({}, { am_company_id: _company_id, page, page_size: pageSize }, sortAndFilterValues);

      return _session.query(query, params)
        .then((response) => {
          if (!isMountedRef.current) return;
          setData(response.rows);
          setTotal(response.totalRows);
          setTotalPages(response.totalPages);
          setCanNextPage(page + 1 < response.totalPages);
          setCanPreviousPage(page > 0);
          onUpdateRef.current && onUpdateRef.current(response.rows);
        })
        .catch((err) => {
          setData([]);
          console.error("Failed to get table data. ", err);
        });
    }, 200)
  ).current;

  const handleWindowResize = useCallback(() => {
    const container = document.getElementById("data-list-container");
    if (!container) return;
    const rect = container.getBoundingClientRect();
    setListHeight(window.innerHeight - rect.top - 30);
  }, []);

  // Mount / unmount
  useEffect(() => {
    window.addEventListener('resize', handleWindowResize);
    handleWindowResize();
    updateData();
    return () => {
      isMountedRef.current = false;
      window.removeEventListener('resize', handleWindowResize);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // update_data watcher — runs after every render to catch value changes even with stable references
  useEffect(() => {
    if (!update_data) return;
    const currentValue = update_data();
    if (prevUpdateDataValueRef.current === null || currentValue !== prevUpdateDataValueRef.current) {
      prevUpdateDataValueRef.current = currentValue;
      updateData();
    }
  });

  // getRowCount callback when total changes
  useEffect(() => {
    getRowCount && getRowCount(total || 0);
  }, [total, getRowCount]);

  const handleSortBy = (col, ev) => {
    ev.preventDefault();
    if (sortBy && sortBy === col) {
      if (sortAsc) {
        setSortAsc(false);
      } else {
        setSortBy(null);
        setSortAsc(null);
      }
    } else {
      setSortBy(col);
      setSortAsc(true);
    }
    updateData();
  };

  const setFilterValue = (key, ev) => {
    const value = ev.target.value;
    const newFilters = { ...filters };
    if (value === "" || value === null) {
      delete newFilters[key];
    } else {
      newFilters[key] = value;
    }
    setFilters(newFilters);
    updateData();
  };

  const gotoPage = (index) => {
    setPage(index);
    setCanNextPage(totalPages != null && index + 1 < totalPages);
    setCanPreviousPage(index > 0);
    updateData();
  };

  const setPageSize = (number) => {
    if (page > 0) {
      const previous_position = page * pageSize + 1;
      setPage(Math.floor(previous_position / number));
    }
    setPageSizeSt(number);

    const lsListCount = JSON.parse(localStorage.getItem('am:data_page_size')) || {};
    lsListCount[props.query] = number;
    localStorage.setItem("am:data_page_size", JSON.stringify(lsListCount));
    updateData();
  };

  const formatDate = (date) => {
    const oldDate = (typeof date === "object") ? date.props.date : date;
    if (!oldDate) return <></>;
    const d = new Date(oldDate);
    const datePart = d.toLocaleDateString('en-us', { month: "numeric", day: "numeric", year: "numeric" });
    const timePart = d.toLocaleTimeString('en-us', { hour: "numeric", minute: "2-digit" });
    return <><span className="dt_year">{datePart}</span><span className="dt_time">{timePart}</span></>;
  };

  // Split rest_props into table_props and row_props
  const table_props = {};
  const row_props = { className: '' };
  const row_bind_functions = [];
  for (const [name, val] of Object.entries(rest_props)) {
    if (name.indexOf('row_') === 0) {
      const stripped_name = name.slice(4);
      if (typeof val === 'function') row_bind_functions.push(stripped_name);
      row_props[stripped_name] = val;
    } else {
      table_props[name] = val;
    }
  }
  if (row_props['onClick']) row_props['className'] += ' clickable';

  let col_count = cols.length;
  if (actions) col_count++;

  return (
    <div
      id="data-list-container"
      className="data-list-container"
      style={{ "height": "auto", "maxHeight": listHeight }}>
      <div className="table-controls">
        <Dropdown isOpen={dropdownOpen} toggle={() => setDropdownOpen(o => !o)}>
          <DropdownToggle size="sm" caret color="light">Show {pageSize}</DropdownToggle>
          <DropdownMenu value={pageSize}>
            {[10, 20, 30, 40, 50, 100].map(size => (
              <DropdownItem key={size} onClick={() => setPageSize(size)}>
                {size}
              </DropdownItem>
            ))}
          </DropdownMenu>
        </Dropdown>
        <div className="pagination-control">
          {(totalPages > 1) && <>
            <em>Page: {page + 1} of {totalPages} </em>
            <ButtonGroup size="sm">
              <Button color="primary" onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
                <i className="bi bi-skip-backward-fill"></i>
              </Button>
              <Button color="primary" onClick={() => gotoPage(page - 1)} disabled={!canPreviousPage}>
                <i className="bi bi-caret-left-fill"></i>
              </Button>
              <Button color="primary" onClick={() => gotoPage(page + 1)} disabled={!canNextPage}>
                <i className="bi bi-caret-right-fill"></i>
              </Button>
              <Button color="primary" onClick={() => gotoPage(totalPages - 1)} disabled={!canNextPage}>
                <i className="bi bi-skip-forward-fill"></i>
              </Button>
            </ButtonGroup>
          </>}
          <em>Total Records: {total}</em>
        </div>
      </div>
      <Table hover {...table_props} className={classNames('data-table', 'styled', extraClasses)}>
        <thead id="table-header-sticky">
          <tr className='headings'>
            {cols.map(({ header, key }, i) =>
              <th className={classNames('sortable',
                { 'sort-asc': sortBy === key && sortAsc,
                  'sort-desc': sortBy === key && !sortAsc })}
                key={i}
                onClick={handleSortBy.bind(null, key)}>
                {header}
              </th>
            )}
            {(actions || actions_header) && <th className="row-actions">{stringOrComponent(actions_header)}</th>}
          </tr>
          <tr className='filters'>
            {cols.map(({ header, key, data_type }, i) => {
              if (data_type === "boolean") {
                return <th key={i}><BooleanRadioSelector handleSelection={setFilterValue.bind(null, key)} col={key} /></th>;
              } else {
                return (
                  <th key={i}>
                    <InputGroup>
                      <Input type="search" value={filters[key] || ''} placeholder={"Search " + header}
                        onChange={setFilterValue.bind(null, key)} />
                      <InputGroupText>
                        <i className="bi bi-search"></i>
                      </InputGroupText>
                    </InputGroup>
                  </th>
                );
              }
            })}
            {(actions || actions_header) && <th className="row-actions"></th>}
          </tr>
        </thead>
        <tbody>
          {data === null &&
            <tr><td colSpan={col_count} className='loading'>Loading</td></tr>}
          {data && !data.length &&
            <tr><td colSpan={col_count} className='empty'>{empty}</td></tr>}
          {data && !!data.length &&
            data.map((item, i) => {
              const item_row_props = { ...row_props };
              for (const fn of row_bind_functions) {
                item_row_props[fn] = item_row_props[fn].bind(null, item);
              }
              return (
                <tr key={i} {...item_row_props}>
                  {cols.map(({ key, render }, j) => {
                    const content = render ? render(item) : item[key];
                    return <td className={key} key={j}>{key.includes("date") ? formatDate(content) : content}</td>;
                  })}
                  {actions &&
                    <td className="actions">
                      {stringOrComponent(actions, item)}
                    </td>
                  }
                </tr>
              );
            })
          }
        </tbody>
      </Table>
    </div>
  );
}

/**
 * display a radio button and translates yes/no to true/false
*/
const BooleanRadioSelector = ({ handleSelection, col }) => {
  const [selectedOption, setSelectedOption] = useState('');

  const handleChange = (event) => {
    setSelectedOption(event.target.value);
    if (event.target.value === "yes") {
      event.target.value = true;
    } else if (event.target.value === "no") {
      event.target.value = false;
    } else {
      event.target.value = "";
    }
    handleSelection(event);
  };

  return (<>
    <div className="boolean-radio-selectors">
      <div>
        <input
          type="radio"
          id={`yesOption${col}`}
          name={`radioOption${col}`}
          value="yes"
          className="custom-radio"
          checked={selectedOption === 'yes'}
          onChange={handleChange}
        />
        <label htmlFor={`yesOption${col}`}><span>Yes</span></label>
      </div>
      <div>
        <input
          type="radio"
          id={`noOption${col}`}
          name={`radioOption${col}`}
          value="no"
          className="custom-radio"
          checked={selectedOption === 'no'}
          onChange={handleChange}
        />
        <label htmlFor={`noOption${col}`}><span>No</span></label>
      </div>
    </div>
    <div className="clear-radios" onClick={handleChange}><span>Clear</span></div>
  </>);
};
